import { useStyletron } from '@thanx/uikit/theme'
import useScrollingTabNav, {
  LEFT_EDGE_WIDTH_PX,
  RIGHT_EDGE_WIDTH_PX,
} from 'hooks/useScrollingTabNav'
import React from 'react'
import Nav from 'react-bootstrap-five/Nav'
// The belizeHole/grey ramps live on the merchant theme, which is a superset of
// the uikit theme that `useStyletron` is typed against — see NavLink.tsx.
import { merchantTheme } from 'theme'

// A drop-in alternative to TabNav for pages with enough tabs to overflow the
// container. TabNav lets the row wrap onto a second line, which stops reading
// as a tab bar; this keeps a single line and scrolls sideways instead.
//
// Per the "in-page report nav" design (sketch.thanx.com/in-page-report-nav):
//   - one line, never wrapping; the scrollbar itself is hidden
//   - a "+N more" control on the right reports how many tabs are still off
//     the right edge, and its count updates live while scrolling; clicking it
//     advances one page (see scrollByPage — this one point departs from the
//     prototype, which jumped to the far end)
//   - the next tab is allowed to peek out from under "+N more", on a solid
//     white backing so the label stays legible — that peek is the cue that
//     there is more to the right
//   - a "‹" appears on the left only once scrolled
//   - clicking a tab scrolls it to the leading (left-most) position, so
//     picking a right-edge tab reveals whatever follows it
//
// Deliberately a separate component rather than a change to TabNav: every
// other tabbed page in the app renders through TabNav, and none of them has
// asked for this behaviour.

type Props = {
  children: React.ReactNode
  className?: string
  // Accessible name for the underlying tablist. `role="tablist"` lands on the
  // inner Nav (via Tab.Container), so the name belongs there — see TabNav.
  ariaLabel?: string
}

// Leaves the led tab clear of the "‹" control once the row has scrolled.

const ScrollingTabNav: React.FC<Props> = ({
  children,
  ariaLabel,
  className = '',
}) => {
  const [css] = useStyletron()
  const {
    scrollRef,
    navRef,
    leftEdgeRef,
    rightEdgeRef,
    hiddenCount,
    scrollByPage,
    handleClickCapture,
    handleFocusCapture,
  } = useScrollingTabNav()

  const edgeBase = {
    position: 'absolute' as const,
    top: '0',
    bottom: '1px',
    display: 'flex',
    alignItems: 'center',
    zIndex: 2,
    // The zones are 98px/150px wide but only their buttons are interactive.
    // Without this the transparent part of a zone sits over real tabs and
    // swallows clicks on them, so a visible tab can be unclickable.
    pointerEvents: 'none' as const,
  }

  const controlBase = {
    background: 'none',
    border: '0',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap' as const,
    color: merchantTheme.colors.belizeHole50,
    // Re-enable hit testing inside the pointer-events:none zone above.
    pointerEvents: 'auto' as const,
    // 44px touch target without letting the control change the row's height.
    minHeight: '44px',
    minWidth: '44px',
    ':hover': {
      // The design's belize-70. Not a palette token yet, so it is spelled out
      // here rather than faked from an existing one.
      color: '#00538A',
    },
    // src/index.scss sets `*:focus { outline: none !important }` app-wide, so
    // an outline here would simply lose. A box-shadow ring is the repo's own
    // workaround and is not subject to that rule.
    ':focus-visible': {
      boxShadow: `0 0 0 2px ${merchantTheme.colors.belizeHole50}`,
      borderRadius: '4px',
    },
    // Windows High Contrast (and any forced-colors mode) drops box-shadows
    // entirely, which would leave these buttons with no visible keyboard focus
    // at all — the box-shadow above is only here because the app-wide
    // `*:focus { outline: none !important }` beats a normal outline. Forced
    // colors also lifts that !important, so an outline works again here, and
    // `CanvasText` is the system colour guaranteed to contrast with the
    // forced background.
    '@media (forced-colors: active)': {
      ':focus-visible': {
        outline: '2px solid CanvasText',
        outlineOffset: '2px',
      },
    },
  }

  return (
    // Border spans the full width, as TabNav's does.
    <div className="border-bottom-1 grey-20-border">
      {/* `container` sits on the SCROLLER rather than the Nav (TabNav puts it
          on the Nav). Two reasons: the row then clips at the same gutters the
          page content uses, and it gives the edge controls a container-width
          positioning context — anchored to the outer full-width element they
          would drift out to the viewport edges on wide screens, far from the
          tabs they belong to. */}
      <div className={`container ${css({ position: 'relative' })}`}>
        <div
          ref={scrollRef}
          onClickCapture={handleClickCapture}
          onFocusCapture={handleFocusCapture}
          className={css({
            overflowX: 'auto',
            overflowY: 'hidden',
            // Animates the scrollLeft assignments in scrollToLeft. Doing it in
            // CSS rather than JS means it degrades to an instant jump where
            // it isn't supported, instead of not moving at all.
            scrollBehavior: 'smooth',
            '@media (prefers-reduced-motion: reduce)': {
              scrollBehavior: 'auto',
            },
            // The row is navigable by chevron, "+N more", click-to-lead, drag
            // and keyboard, so the bar itself is noise.
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '::-webkit-scrollbar': { display: 'none' },
          })}
        >
          {/* `scrolling-tab-nav` (_tabs.scss) stops the row wrapping AND stops
              the items shrinking — without the latter the tabs compress to fit
              and each label wraps internally, so the row never overflows and
              never scrolls. */}
          <Nav
            ref={navRef}
            className={`nav-tabs border-none scrolling-tab-nav ${className}`}
            aria-label={ariaLabel}
          >
            {children}
          </Nav>
        </div>

        <div
          ref={leftEdgeRef}
          hidden
          className={css({
            ...edgeBase,
            left: '0',
            width: `${LEFT_EDGE_WIDTH_PX}px`,
            justifyContent: 'flex-start',
            paddingLeft: '12px',
            backgroundImage: `linear-gradient(270deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 42%, ${merchantTheme.colors.white} 70%, ${merchantTheme.colors.white} 100%)`,
          })}
        >
          <button
            type="button"
            aria-label="Scroll tabs left"
            onClick={() => scrollByPage(-1)}
            className={css({
              ...controlBase,
              padding: '6px',
              borderRadius: '4px',
              ':hover': {
                color: '#00538A',
                backgroundColor: merchantTheme.colors.belizeHole10,
              },
            })}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>

        <div
          ref={rightEdgeRef}
          className={css({
            ...edgeBase,
            right: '0',
            width: `${RIGHT_EDGE_WIDTH_PX}px`,
            justifyContent: 'flex-end',
            paddingRight: '16px',
            // Transparent at first so the next tab shows through, then fully
            // opaque BEFORE the label starts, so the peek fades out instead of
            // being hard-clipped and the label never sits over letters. The
            // design's 52% solid-point assumed a narrower stage; the label here
            // begins around 44% of this zone, so the fade has to finish sooner.
            backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0) 12%, ${merchantTheme.colors.white} 40%, ${merchantTheme.colors.white} 100%)`,
          })}
        >
          <button
            type="button"
            // The visible text is a substring of the accessible name, so the
            // two agree (WCAG 2.5.3 Label in Name) while the name still says
            // what the control does. A bare `aria-label` would have hidden
            // the count from assistive tech entirely.
            aria-label={`+${hiddenCount} more report tabs`}
            onClick={() => scrollByPage(1)}
            className={css({
              ...controlBase,
              gap: '3px',
              fontWeight: 700,
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: '1px',
              textTransform: 'uppercase',
            })}
          >
            +{hiddenCount}&nbsp;more
          </button>
        </div>
      </div>
    </div>
  )
}

export default ScrollingTabNav
